import { Check, Copy } from "lucide-react";
import { useState } from "react";

const CURL_SNIPPET = `curl -N -X POST \\
  "https://api.anter.ai/v1/external/agent-runner/agents/:id/run-stream" \\
  -H "x-project-id: proj_..." \\
  -H "x-api-key: proj_api-key_..." \\
  -H "Content-Type: application/json" \\
  -d '{"message": "Summarize the key risks for this quarter.", "userId": "user_123"}'`;

const NODE_SNIPPET = `const res = await fetch(
  "https://api.anter.ai/v1/external/agent-runner/agents/:id/run-stream",
  {
    method: "POST",
    headers: {
      "x-project-id": "proj_...",
      "x-api-key": "proj_api-key_...",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      message: "Summarize this quarter's key risks.",
      userId: "user_123",
    }),
  }
);

// SSE stream — consume with ReadableStream or EventSource
for await (const chunk of res.body) { /* ... */ }`;

type Tab = "curl" | "node";

function highlight(code: string, lang: Tab) {
  if (lang === "curl") {
    return code
      .replace(/(curl|-N|-X POST|-H|-d)/g, '<span class="text-blue">$1</span>')
      .replace(
        /("(x-project-id|x-api-key|Content-Type|x-api-key)[^"]*")/g,
        '<span class="text-green">$1</span>'
      )
      .replace(/(proj_[^\s'"\\]+)/g, '<span class="text-primary-text">$1</span>')
      .replace(/(https?:\/\/[^\s'"\\]+)/g, '<span class="text-text-muted">$1</span>');
  }
  return code
    .replace(/(const|await|for|of)/g, '<span class="text-blue">$1</span>')
    .replace(
      /("(x-project-id|x-api-key|Content-Type)[^"]*")/g,
      '<span class="text-green">$1</span>'
    )
    .replace(/(proj_[^\s'"\\,)]+)/g, '<span class="text-primary-text">$1</span>')
    .replace(/(\/\/[^\n]+)/g, '<span class="text-text-faint">$1</span>');
}

export default function ApiSnippet() {
  const [tab, setTab] = useState<Tab>("curl");
  const [copied, setCopied] = useState(false);

  const code = tab === "curl" ? CURL_SNIPPET : NODE_SNIPPET;

  function copy() {
    void navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <section className="py-20">
      <div className="mb-12">
        <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-primary-text">
          Ship fast
        </p>
        <h2 className="max-w-2xl text-3xl font-extrabold tracking-tight text-text sm:text-4xl">
          One endpoint.
          <br />
          Your agent, streaming anywhere.
        </h2>
        <p className="mt-4 max-w-xl text-base leading-relaxed text-text-muted">
          A project API key and a single{" "}
          <code className="rounded bg-bg-secondary px-1 font-mono text-sm text-primary-text">
            POST /run-stream
          </code>{" "}
          call. SSE chunks arrive in real time. Reconnect from any index with the replay endpoint.
        </p>
      </div>

      <div className="overflow-hidden rounded-2xl border border-border bg-bg-secondary/80">
        {/* Tab bar */}
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div className="flex gap-1">
            {(["curl", "node"] as Tab[]).map((t) => (
              <button
                key={t}
                type="button"
                aria-pressed={tab === t}
                onClick={() => setTab(t)}
                className={`rounded-md px-3 py-1 font-mono text-xs font-semibold transition ${
                  tab === t ? "bg-primary-dim text-primary-text" : "text-text-faint hover:text-text"
                }`}
              >
                {t === "curl" ? "cURL" : "Node.js"}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={copy}
            className="flex min-h-8 items-center gap-1.5 rounded-md px-2 py-1 text-xs text-text-faint transition hover:text-text"
            aria-label={copied ? "Code copied" : "Copy code"}
          >
            {copied ? (
              <Check className="h-3.5 w-3.5 text-green" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>

        {/* Code */}
        <div className="overflow-x-auto p-5">
          <pre className="font-mono text-xs leading-relaxed text-text sm:text-sm">
            <code dangerouslySetInnerHTML={{ __html: highlight(code, tab) }} />
          </pre>
        </div>
      </div>

      {/* Streaming note */}
      <p className="mt-4 text-xs text-text-faint">
        The stream supports both the Vercel AI SDK format (
        <code className="font-mono">Accept: ai-sdk=v1</code>) and a plain SSE format — pick what
        fits your client stack.
      </p>
    </section>
  );
}
